import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import { APP_CONFIG } from "../../config/appConfig";
import { getUserByPhone, linkSocialToExistingUser } from "../../services/userProfileService";
import { auth } from "../../firebase/config";

const PRIMARY = "#4A6741";

function normalizePhone(raw) {
  return raw.replace(/[^0-9]/g, "");
}

function isTestNumber(phone) {
  const n = normalizePhone(phone);
  const { start, end } = APP_CONFIG.testRange;
  return n >= start && n <= end;
}

function toE164(phone) {
  const n = normalizePhone(phone);
  if (n.startsWith("0")) return "+82" + n.slice(1);
  return "+" + n;
}

const PhoneVerifyPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromSocial = location.state?.fromSocial || false;

  const [phone, setPhone] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState("");
  const [serverCode, setServerCode] = useState("");
  const [testCode, setTestCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [timer, setTimer] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  const startTimer = () => {
    setTimer(180);
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) { clearInterval(intervalRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendCode = async () => {
    const normalized = normalizePhone(phone);
    if (normalized.length < 10) {
      setError("올바른 전화번호를 입력해주세요.");
      return;
    }
    setBusy(true);
    setError("");

    if (isTestNumber(normalized)) {
      const genCode = String(Math.floor(100000 + Math.random() * 900000));
      setTestCode(genCode);
      setServerCode(genCode);
      setCodeSent(true);
      startTimer();
      setBusy(false);
      return;
    }

    const genCode = String(Math.floor(100000 + Math.random() * 900000));
    setServerCode(genCode);

    const cfUrl = APP_CONFIG.sms.cfUrl;
    if (!cfUrl) {
      setError("SMS 서비스가 아직 설정되지 않았습니다.");
      setBusy(false);
      return;
    }

    try {
      const res = await fetch(cfUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: normalized, authcode: genCode, label: APP_CONFIG.sms.label }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "SMS 발송 실패");
      setCodeSent(true);
      startTimer();
    } catch (err) {
      setError(err.message || "SMS 발송에 실패했습니다.");
    } finally {
      setBusy(false);
    }
  };

  const handleVerify = async () => {
    if (!code.trim()) {
      setError("인증번호를 입력해주세요.");
      return;
    }
    if (serverCode && code.trim() !== serverCode) {
      setError("인증번호가 일치하지 않습니다. 재전송 해주세요.");
      setCode('');
      setServerCode('');
      setTestCode('');
      if (intervalRef.current) clearInterval(intervalRef.current);
      setTimer(0);
      return;
    }

    setBusy(true);
    setError("");

    const phoneE164 = toE164(phone);

    try {
      const existingUser = await getUserByPhone(phoneE164);

      if (existingUser && fromSocial && auth.currentUser) {
        // 소셜 로그인 후 전화인증 → 기존 계정에 소셜 연결
        await linkSocialToExistingUser({
          existingUid: existingUser.uid,
          socialUid: auth.currentUser.uid,
        });

        if (existingUser.status === "approved") {
          navigate("/home", { replace: true });
        } else {
          navigate("/login", { replace: true, state: { toast: "승인 대기중입니다. 관리자 승인 후 이용 가능합니다." } });
        }
      } else if (existingUser && !fromSocial) {
        // 일반 회원가입 → 기존 계정 있음 → 아이디/비번만 추가 입력
        navigate("/signup", {
          replace: true,
          state: {
            verifiedPhone: phoneE164,
            existingAccount: true,
          },
        });
      } else {
        // 신규 가입
        navigate("/signup", {
          replace: true,
          state: {
            verifiedPhone: phoneE164,
            fromSocial,
            socialEmail: auth.currentUser?.email || "",
            socialName: auth.currentUser?.displayName || "",
            socialUid: auth.currentUser?.uid || "",
          },
        });
      }
    } catch (err) {
      console.error("전화번호 확인 실패:", err);
      setError("처리 중 오류가 발생했습니다.");
    } finally {
      setBusy(false);
    }
  };

  const formatTimer = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  return (
    <Container>
      <Title>전화번호 인증</Title>
      <Desc>전화번호 인증이 필요합니다.</Desc>

      <FormArea>
        <Label>전화번호</Label>
        <Input
          type="tel"
          placeholder="01012345678"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          disabled={codeSent}
        />
        <SendBtn onClick={handleSendCode} disabled={busy || (codeSent && timer > 0)}>
          {codeSent ? "재전송" : "인증번호 전송"}
        </SendBtn>

        {codeSent && (
          <>
            <Label>인증번호 {timer > 0 && <TimerText>{formatTimer(timer)}</TimerText>}</Label>
            <Input
              type="text"
              inputMode="numeric"
              placeholder="6자리 인증번호"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            {testCode && <TestCodeBox>테스트 코드: {testCode}</TestCodeBox>}
          </>
        )}

        {error && <ErrorText>{error}</ErrorText>}

        {codeSent && (
          <VerifyBtn onClick={handleVerify} disabled={busy}>
            {busy ? "확인 중..." : "인증 완료"}
          </VerifyBtn>
        )}

        <SkipBtn onClick={() => navigate("/login", { replace: true })}>
          돌아가기
        </SkipBtn>
      </FormArea>
    </Container>
  );
};

export default PhoneVerifyPage;

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #EEF2EE 0%, #F4F3F8 40%);
  padding: 80px 24px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled.p`
  font-size: 22px;
  text-align: center;
  margin-bottom: 8px;
  color: #1a1a1a;
`;

const Desc = styled.p`
  font-size: 15px;
  color: #888;
  text-align: center;
  margin-bottom: 40px;
`;

const FormArea = styled.div`
  width: 100%;
  max-width: 480px;
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  margin-bottom: 10px;
  margin-top: 20px;
  color: #333;
`;

const TimerText = styled.span`
  color: ${PRIMARY};
  font-variant-numeric: tabular-nums;
`;

const Input = styled.input`
  width: 100%;
  padding: 16px 18px;
  border: 1px solid #ddd;
  border-radius: 10px;
  font-size: 16px;
  &:focus { border-color: ${PRIMARY}; outline: none; }
  &:disabled { background: #f5f5f5; }
`;

const SendBtn = styled.button`
  width: 100%;
  padding: 16px;
  margin-top: 12px;
  border-radius: 28px;
  background: ${PRIMARY};
  color: white;
  font-size: 16px;
  &:disabled { opacity: 0.5; }
`;

const VerifyBtn = styled.button`
  width: 100%;
  padding: 16px;
  margin-top: 24px;
  border-radius: 28px;
  background: ${PRIMARY};
  color: white;
  font-size: 16px;
  &:disabled { opacity: 0.5; }
`;

const SkipBtn = styled.button`
  width: 100%;
  padding: 14px;
  margin-top: 12px;
  background: none;
  color: #999;
  font-size: 14px;
  text-decoration: underline;
`;

const TestCodeBox = styled.div`
  margin-top: 10px;
  padding: 12px;
  background: #FFF3E0;
  border-radius: 10px;
  font-size: 14px;
  color: #E65100;
  text-align: center;
`;

const ErrorText = styled.p`
  color: #EF5350;
  font-size: 14px;
  margin-top: 10px;
`;
